import { ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Formik, getIn } from 'formik'

import { Column } from '@10xLabs/react-web/components/Column'
import { Row } from '@10xLabs/react-web/components/Row'
import { VehicleIcon } from 'components/Icons/VehicleIcon'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { InputDate } from 'components/v2/InputDate'
import { ServiceInput } from 'features/Services/components/NewServicesV2/components/ServicesSelector'
import {
  ItinerariesGrid,
  ItinerariesGridItem,
  ItineraryForm
} from 'features/Services/components/NewServicesV2/components/ItinerariesCard'
import { VehicleAutocomplete } from 'features/Vehicles/components/VehicleAutocomplete'
import { GTRouteServiceSingleTripReason, GTVehicleSuggestion } from 'gql/types.generated'
import { getVehicleLabelWithExternalId } from 'features/Vehicles/utils'
import { verifyFormik } from 'utils/form'
import { servicesFormSchema } from './schema'
import { IItineraryAndVehicleFormProps, IItineraryAndVehicleFormValues } from './types'
import { Select } from 'components/v2/Select'
import { IItinerary } from 'features/Services/components/NewServicesV2/components/ItinerariesCard/types'

const DEFAULT_SERVICE_CODE = 'KT001'

const messages = defineMessages({
  dateLabel: {
    id: 'trips.create.itineraryAndVehicleForm.date.label',
    defaultMessage: 'Date'
  },
  vehiclePlaceholder: {
    id: 'trips.create.itineraryAndVehicleForm.vehicle.placeholder',
    defaultMessage: 'Select a vehicle'
  },
  reasonPlaceholder: {
    id: 'trips.create.itineraryAndVehicleForm.reason.placeholder',
    defaultMessage: 'Select an option'
  },
  maintenanceReason: {
    id: 'trips.create.itineraryAndVehicleForm.reason.maintenance',
    defaultMessage: 'Maintenance'
  },
  operationalReason: {
    id: 'trips.create.itineraryAndVehicleForm.reason.operational',
    defaultMessage: 'Operation'
  }
})

export const ItineraryAndVehicleForm = ({
  service,
  loading,
  error,
  onCancel,
  onSubmit
}: IItineraryAndVehicleFormProps): ReactElement => {
  const intl = useIntl()

  const initialValues: IItineraryAndVehicleFormValues = {
    reason: '',
    services: [
      {
        ...service.services[0],
        code: DEFAULT_SERVICE_CODE
      }
    ]
  }

  return (
    <Formik initialValues={initialValues} validationSchema={servicesFormSchema(intl)} onSubmit={onSubmit}>
      {({ values, handleSubmit, setFieldValue, handleChange, handleBlur, touched, errors }): ReactElement => {
        const handleCancel = (): void => {
          onCancel(values)
        }
        const currentItineraryString = 'services[0].itineraries[0]'
        const currentItineraryArray = ['services', '0', 'itineraries', '0']
        const itinerary = values.services?.at(0)?.itineraries[0] as IItinerary
        const itineraryTouched = getIn(touched, currentItineraryString)
        const currentVehicleString = 'services[0].vehicles[0]'
        const currentVehicleArray = ['services', '0', 'vehicles', '0']
        const vehicle = values.services?.at(0)?.vehicles[0]
        const today = new Date()
        const disabledDays = {
          before: today
        }

        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit} isLargeContent>
            <Heading.H3>
              <FormattedMessage id="services.new.title" defaultMessage="Create trip" />
            </Heading.H3>
            <ServiceInput
              value={values.services?.at(0)?.code}
              onChange={(): void => {
                return
              }}
              isActive
              small
            />
            <FormLayout.Divider />
            <FormLayout.Card small>
              <FormLayout.CardHeader>
                <Heading.H4>
                  <FormattedMessage id="services.form.section.itineraries.title" defaultMessage="Itinerary" />
                </Heading.H4>
              </FormLayout.CardHeader>
              <ItinerariesGrid>
                <ItinerariesGridItem>
                  <Row>
                    <Column width={'calc(100% / 3)'}>
                      <InputDate
                        intl={intl}
                        id={`${currentItineraryString}.startDate`}
                        label={intl.formatMessage(messages.dateLabel)}
                        placeholder="--/--/----"
                        value={itinerary.startDate ?? ''}
                        onChange={async (value): Promise<void> => {
                          await setFieldValue(`${currentItineraryString}.startDate`, value)
                          setFieldValue(`${currentVehicleString}.startDate`, value)
                        }}
                        disabledDays={disabledDays}
                        touched={itineraryTouched?.startDate}
                        errors={verifyFormik<string>(errors, [...currentItineraryArray, 'startDate'])}
                      />
                    </Column>
                  </Row>
                  <FormLayout.Divider />
                  <ItineraryForm
                    itinerary={values.services?.at(0)?.itineraries[0] as IItinerary}
                    currentItineraryString={currentItineraryString}
                    currentItineraryArray={currentItineraryArray}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </ItinerariesGridItem>
              </ItinerariesGrid>
            </FormLayout.Card>
            <FormLayout.Card>
              <FormLayout.CardHeader>
                <Heading.H4>
                  <FormattedMessage id="services.form.section.vehicles.title" defaultMessage="Vehicle Settings" />
                </Heading.H4>
              </FormLayout.CardHeader>
              <FormLayout.Divider />
              <Row>
                <Column width="calc(100% / 3)">
                  <VehicleAutocomplete
                    companyId={service.company?.id ?? ''}
                    id={`${currentVehicleString}.vehicle.id`}
                    name={`${currentVehicleString}.vehicle.id`}
                    label={
                      <FormattedMessage
                        id="trips.create.itineraryAndVehicleForm.vehicle.label"
                        defaultMessage="Vehicle"
                      />
                    }
                    placeholder={intl.formatMessage(messages.vehiclePlaceholder)}
                    iconLeft={<VehicleIcon />}
                    value={getVehicleLabelWithExternalId({
                      name: vehicle?.vehicle?.name || '',
                      externalID: vehicle?.vehicle?.externalID || ''
                    })}
                    onChange={(vehicle: GTVehicleSuggestion): void => {
                      setFieldValue(`${currentVehicleString}.vehicle`, {
                        id: vehicle.id,
                        name: vehicle.name,
                        externalID: vehicle.externalID
                      })
                    }}
                    touched={verifyFormik<boolean>(touched, [...currentVehicleArray, 'vehicle', 'id'])}
                    errors={verifyFormik<string>(errors, [...currentVehicleArray, 'vehicle', 'id'])}
                    isActive
                  />
                </Column>
              </Row>
            </FormLayout.Card>
            <FormLayout.Card>
              <FormLayout.CardHeader>
                <Heading.H4>
                  <FormattedMessage id="services.form.section.reason.title" defaultMessage="Reason" />
                </Heading.H4>
              </FormLayout.CardHeader>
              <Row>
                <Column width="calc(100% / 3)">
                  <Select
                    label={
                      <FormattedMessage
                        id="trips.create.itineraryAndVehicleForm.reason.reasonPlaceholder"
                        defaultMessage="Category"
                      />
                    }
                    value={values.reason}
                    onChange={(value): void => {
                      setFieldValue('reason', value.target.value)
                    }}
                    errors={errors.reason}
                    touched={touched.reason}
                    onBlur={handleBlur}
                  >
                    <option value="">{intl.formatMessage(messages.reasonPlaceholder)}</option>
                    <option value={GTRouteServiceSingleTripReason.Maintenance}>
                      {intl.formatMessage(messages.maintenanceReason)}
                    </option>
                    <option value={GTRouteServiceSingleTripReason.Operation}>
                      {intl.formatMessage(messages.operationalReason)}
                    </option>
                  </Select>
                </Column>
              </Row>
            </FormLayout.Card>
            {error && (
              <FormLayout.Actions>
                <ErrorMessage>
                  <FormattedMessage
                    id="trips.create.itineraryAndVehicleForm.error"
                    defaultMessage="There was an error while creating your trip"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button type="button" kind="cancel" onClick={handleCancel}>
                <FormattedMessage id="dictionary.back" defaultMessage="Back" />
              </Button>
              <Button type="submit" kind="primary" loading={loading}>
                <FormattedMessage id="dictionary.create" defaultMessage="Create" />
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
